"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MultiSelect = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _loggingStateService = require("@smartsheet/logging-state-service");
var _fuzzysort = _interopRequireDefault(require("fuzzysort"));
var _react = require("react");
var _i18n = require("../../i18n/i18n");
var _logging = require("../../util/logging");
var _MultiSelectBasic = require("./components/multiselect/MultiSelectBasic");
var _MultiSelectComboBox = require("./components/multiselect/MultiSelectComboBox");
var _constants = require("./constants");
var _SelectCatalog = require("./i18n/SelectCatalog");
var _utils = require("./utils");
/** @jsxRuntime classic */
/** @jsx jsx */

// TODO it'd be really nice to get this HTMLElement generic a bit cleaner for consumers.
//A Type union for all things related to focus and refs might fit nicely.
const MultiSelect = _ref => {
  let {
    clientId,
    enableSelectAll = true,
    innerRef,
    isOptionDisabled,
    isLoading = false,
    isReadonly = false,
    isClearable = false,
    isSearchable,
    locale,
    maxMenuHeight = 200,
    maxOptionsUntilVirtualization = 50,
    minHeight,
    maxSelectedRows = 3,
    rollupSelectionAfter = 50,
    onChange,
    optionHeight,
    useFuzzySearch = false,
    options: providedOptions,
    optionToString = _utils.internalOptionToString,
    menuId: providedMenuId,
    filterOption: providedFilterOption,
    inputValue,
    setInputValue,
    hideDropdownArrow = false,
    placeholder,
    ...rest
  } = _ref;
  if (process.env.NODE_ENV !== "production") {
    const hiddenInputNameDefined = typeof rest.hiddenInputName === "string";
    const getHiddenInputValueDefined = typeof rest.getHiddenInputValue === "function";
    if (hiddenInputNameDefined !== getHiddenInputValueDefined) {
      console.warn("MultiSelect: To use the hidden input, you must include both" + " props: getHiddenInputValue and hiddenInputName");
    }
  }
  (0, _SelectCatalog.injectCatalog)();
  const onChangeWithLogging = (0, _logging.useLoggingEnabledByDefault)((0, _logging.useLoggingDecorator)({
    controlType: "select",
    clientId
  }), {
    actionId: _loggingStateService.LogActionId.CLICK
  }, onChange);
  const defaultFilterOption = (0, _react.useCallback)((opt, input, fuzzySearchScoreMap) => {
    if (useFuzzySearch) {
      const result = _fuzzysort.default.single(input, optionToString(opt));
      if (result) {
        if (fuzzySearchScoreMap) {
          fuzzySearchScoreMap.set(opt, result.score);
        }
        return result.score >= _constants.FUZZYSORT_MIN_THRESHOLD;
      }
      return false;
    } else {
      return optionToString(opt)?.toLowerCase().startsWith(input);
    }
  }, [optionToString, useFuzzySearch]);
  const memoizedOptions = (0, _react.useMemo)(() => {
    // Don't enable select all if there are no options.
    if (enableSelectAll && providedOptions.length > 0) {
      return [_utils.selectAllMarkerOption, ...providedOptions];
    } else {
      return providedOptions;
    }
  }, [enableSelectAll, providedOptions]);

  // Filters out all non-selectable options from being selected with `selectAll`
  const selectableOptions = (0, _react.useMemo)(() => {
    if (enableSelectAll && providedOptions.length > 0) {
      return providedOptions.filter(option => !isOptionDisabled?.(option) && !(0, _utils.isSelectV2OptionSeparator)(option));
    } else {
      return providedOptions;
    }
  }, [enableSelectAll, isOptionDisabled, providedOptions]);
  const menuId = (0, _react.useMemo)(() => providedMenuId || (0, _lodestarUtils.genId)("select-menu"), [providedMenuId]);
  const sharedProps = {
    memoizedOptions: memoizedOptions,
    onChange: onChangeWithLogging,
    options: selectableOptions,
    clientId,
    isOptionDisabled,
    enableSelectAll,
    isClearable,
    isLoading,
    isReadonly,
    isSearchable,
    locale,
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    minHeight,
    maxSelectedRows,
    rollupSelectionAfter,
    optionHeight,
    optionToString,
    menuId,
    filterOption: providedFilterOption || defaultFilterOption,
    hideDropdownArrow
  };
  const isComboBox = isSearchable || typeof rest.onCreateOption === "function";
  return (0, _core.jsx)(_i18n.I18nContextProvider, {
    locale: locale
  }, isComboBox ? (0, _core.jsx)(_MultiSelectComboBox.MultiSelectComboBox, (0, _extends2.default)({}, rest, sharedProps, {
    inputValue: inputValue,
    setInputValue: setInputValue,
    innerRef: innerRef,
    placeholder: placeholder,
    useFuzzySearch: useFuzzySearch
  })) : (0, _core.jsx)(_MultiSelectBasic.MultiSelectBasic, (0, _extends2.default)({}, rest, sharedProps, {
    innerRef: innerRef,
    placeholder: placeholder
  })));
};
exports.MultiSelect = MultiSelect;
//# sourceMappingURL=MultiSelect.js.map